/* eslint-disable @typescript-eslint/no-explicit-any */
import axios, {
  AxiosError,
  AxiosInstance,
  AxiosPromise,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { TokenService } from './TokenService';

let isRefreshing = false;
let failedQueue: any = [];

const processQueue = (error: AxiosError | null, token: string | null): void => {
  failedQueue.forEach((prom: any) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const REFRESH_TOKEN_URL = '/auth/api/v1/refresh';

const setTokenInterceptors = (instance: AxiosInstance): void => {
  instance.interceptors.request.use(
    async (config: AxiosRequestConfig & { headers?: any }) => {
      const token = TokenService.getAccessToken();
      if (token) {
        config.headers = {
          ...config.headers,
          Authorization: `Bearer ${token}`,
        };
      }
      return config;
    },
    error => Promise.reject(error),
  );

  instance.interceptors.response.use(
    response => {
      const accessToken = response.headers['access-token'];
      const refreshToken = response.headers['refresh-token'];

      if (accessToken) {
        TokenService.setAccessToken(accessToken);
      }

      if (refreshToken) {
        TokenService.setRefreshToken(refreshToken);
      }

      return response;
    },
    async error => {
      const originalRequest = error.config;

      // Логирование ошибок
      console.error('Error during request:', error);

      if (
        error.response?.status === 401 ||
        (error.response?.status === 403 &&
          !originalRequest._retry &&
          originalRequest.url !== REFRESH_TOKEN_URL)
      ) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              if (!originalRequest.headers) {
                originalRequest.headers = {};
              }
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return instance(originalRequest);
            })
            .catch(err => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const refreshToken = TokenService.getRefreshToken();
          if (!refreshToken) {
            throw new Error('No refresh token available');
          }

          const response: AxiosResponse = await instance.post(REFRESH_TOKEN_URL, {
            refresh_token: refreshToken,
          });

          const newAccessToken = response.headers['access-token'];
          const newRefreshToken = response.headers['refresh-token'];

          if (newAccessToken && newRefreshToken) {
            TokenService.setAccessToken(newAccessToken);
            TokenService.setRefreshToken(newRefreshToken);

            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

            processQueue(null, newAccessToken);
            return instance(originalRequest);
          } else {
            throw new Error('Refresh token response did not include new tokens');
          }
        } catch (err: any) {
          processQueue(err, null);
          return Promise.reject(err);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    },
  );
};

export class Api {
  static instance: Api;

  axiosInstance: AxiosInstance;

  constructor() {
    this.axiosInstance = axios.create({
      timeout: 30000,
      baseURL: process.env.REACT_APP_API_URL,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    });

    setTokenInterceptors(this.axiosInstance);
  }

  static getInstance(): Api {
    if (!Api.instance) {
      Api.instance = new Api();
    }
    return Api.instance;
  }

  static getAxios(): AxiosInstance {
    return Api.getInstance().axiosInstance;
  }

  static getImage(url: string, config: AxiosRequestConfig = {}): AxiosPromise<Blob> {
    return Api.getAxios().get(url, {
      ...config,
      responseType: 'blob',
      headers: {
        Accept: 'image/png',
        ...config.headers,
      },
    });
  }

  static get<T = any>(
    url: string,
    params: Record<string, any> = {},
    config: AxiosRequestConfig = {},
  ): AxiosPromise<T> {
    return Api.getAxios().get(url, { params, ...config });
  }

  static delete<T = any>(
    url: string,
    params: Record<string, any> = {},
    config: AxiosRequestConfig = {},
  ): AxiosPromise<T> {
    return Api.getAxios().delete(url, { params, ...config });
  }

  static post<T = any>(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): AxiosPromise<T> {
    return Api.getAxios().post(url, data, config);
  }

  static put<T = any>(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): AxiosPromise<T> {
    return Api.getAxios().put(url, data, config);
  }

  static patch<T = any>(
    url: string,
    data?: Record<string, any>,
    config?: AxiosRequestConfig,
  ): AxiosPromise<T> {
    return Api.getAxios().patch(url, data, config);
  }
}
